import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Navigate, Link } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Input,
  Select,
  Space,
  Typography,
  Spin,
  Button,
  Modal,
  Form,
  InputNumber,
  Table,
  Tag,
  Divider,
  DatePicker,
  Statistic,
  Empty,
  Descriptions,
  Badge,
  Tooltip,
  Tabs,
  Upload,
  Grid,
} from "antd";
import {
  SendOutlined,
  ReloadOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  UploadOutlined,
  FileAddOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import {
  getReadyForDispatch,
  createDispatch,
  addItemsToDispatch,
  getAllDispatches,
  getDispatchById,
} from "../../api/dispatchApi";
import PageTitle from "../page-header/PageHeader";
import checkTokenExp from "../../utils/checkTokenExp";
import moment from "moment";

const { Title, Text } = Typography;

const DispatchPlanning = () => {
  const screens = Grid.useBreakpoint();
  const isLogged = Boolean(localStorage.getItem("isLogged"));
  const accessToken = localStorage.getItem("access-token");

  const [activeTab, setActiveTab] = useState("ready");
  const [loading, setLoading] = useState(false);

  // Ready for dispatch
  const [readyData, setReadyData] = useState(null);

  // Dispatch history
  const [dispatches, setDispatches] = useState([]);
  const [dispatchDetail, setDispatchDetail] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [historySearch, setHistorySearch] = useState("");
  const [historyCustomerFilter, setHistoryCustomerFilter] = useState(undefined);

  // Create dispatch form
  const [dispatchModalVisible, setDispatchModalVisible] = useState(false);
  const [dispatchForm] = Form.useForm();
  const [dispatchItems, setDispatchItems] = useState([]);
  const [selectedJobsByOrder, setSelectedJobsByOrder] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [receivingFile, setReceivingFile] = useState(null);

  // Add to existing dispatch form
  const [existingDispatchModalVisible, setExistingDispatchModalVisible] = useState(false);
  const [existingDispatchForm] = Form.useForm();
  const [existingDispatchItems, setExistingDispatchItems] = useState([]);
  const [dispatchOptions, setDispatchOptions] = useState([]);
  const [dispatchOptionsLoading, setDispatchOptionsLoading] = useState(false);
  const [submittingExisting, setSubmittingExisting] = useState(false);

  const buildDispatchItem = useCallback((job, orderGroup) => {
    const orderedQty = job.ordered_qty || 0;
    const dispatchedQty = job.dispatched_qty || 0;
    const remainingQty = Math.max(
      0,
      job.remaining_qty ?? orderedQty - dispatchedQty
    );

    return {
      job_id: job.id,
      job_number: job.job_number,
      item_name: job.item_name,
      ordered_qty: orderedQty,
      dispatched_qty: dispatchedQty,
      remaining_qty: remainingQty,
      qty_dispatched: remainingQty,
      customer_id: orderGroup.customer?.id,
      customer_name: orderGroup.customer?.name,
      order_id: orderGroup.order_id,
      order_number: orderGroup.order_number,
      po_number: orderGroup.po_number,
    };
  }, []);

  // ── Data Loading ───────────────────────────────────────────
  const fetchReady = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getReadyForDispatch();
      setReadyData(data);
    } catch {
      toast.error("Failed to load dispatch data");
    }
    setLoading(false);
  }, []);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAllDispatches({ count: 500 });
      setDispatches(data.data || data.dispatches || data || []);
    } catch {
      toast.error("Failed to load dispatch history");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (activeTab === "ready") fetchReady();
    else fetchHistory();
  }, [activeTab, fetchReady, fetchHistory]);


  // ── Dispatch Form Handlers ─────────────────────────────────
  const getSelectedJobs = useCallback(() => {
    const selectedIds = new Set(Object.values(selectedJobsByOrder).flat());
    if (!selectedIds.size) return [];

    return (readyData?.orders || []).flatMap((orderGroup) =>
      (orderGroup.jobs || [])
        .filter((job) => selectedIds.has(job.id))
        .map((job) => buildDispatchItem(job, orderGroup))
    );
  }, [buildDispatchItem, readyData, selectedJobsByOrder]);

  const openDispatchForm = (selectedRows = null) => {
    const jobsToDispatch = selectedRows || getSelectedJobs();
    if (!jobsToDispatch.length) {
      toast.error("Select at least one job to add a dispatch");
      return;
    }

    setDispatchModalVisible(true);
    setDispatchItems(jobsToDispatch);
    dispatchForm.resetFields();
    dispatchForm.setFieldsValue({
      dispatched_on: moment(),
      delivery_type: "single",
    });
    setReceivingFile(null);
  };

  const handleDispatchSubmit = async () => {
    try {
      const values = await dispatchForm.validateFields();
      if (
        !dispatchItems.length ||
        dispatchItems.every((i) => !i.qty_dispatched)
      ) {
        return toast.error("Enter dispatch quantity for at least one job");
      }

      const selectedCustomerIds = [
        ...new Set(
          dispatchItems
            .filter((i) => i.qty_dispatched > 0)
            .map((i) => i.customer_id)
            .filter(Boolean)
        ),
      ];

      if (values.delivery_type !== "multi_point" && selectedCustomerIds.length > 1) {
        return toast.error(
          "Single point dispatch can include jobs from only one customer"
        );
      }

      const payload = {
        customer_id: selectedCustomerIds[0],
        receipt_type: "challan",
        receipt_number: values.challan_number,
        delivery_type: values.delivery_type || "single",
        dispatch_date: values.dispatched_on
          ? values.dispatched_on.toISOString()
          : new Date().toISOString(),
        notes: values.notes,
        receiving_image: receivingFile,
        items: dispatchItems
          .filter((i) => i.qty_dispatched > 0)
          .map((i) => ({
            job_id: i.job_id,
            qty_dispatched: i.qty_dispatched,
          })),
      };

      setSubmitting(true);
      const result = await createDispatch(payload);
      toast.success(`Dispatch ${result.dispatch_number} created!`);
      setDispatchModalVisible(false);
      setDispatchItems([]);
      dispatchForm.resetFields();
      setSelectedJobsByOrder({});
      setReceivingFile(null);
      fetchReady();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create dispatch");
    } finally {
      setSubmitting(false);
    }
  };

  const openExistingDispatchForm = async (selectedRows = null) => {
    const jobsToDispatch = selectedRows || getSelectedJobs();
    if (!jobsToDispatch.length) {
      toast.error("Select at least one job to add to an existing dispatch");
      return;
    }

    setExistingDispatchModalVisible(true);
    setExistingDispatchItems(jobsToDispatch);
    existingDispatchForm.resetFields();
    existingDispatchForm.setFieldsValue({ dispatched_on: moment() });

    const customerIds = [
      ...new Set(jobsToDispatch.map((i) => i.customer_id).filter(Boolean)),
    ];
    setDispatchOptionsLoading(true);
    try {
      const params =
        customerIds.length === 1
          ? { customer_id: customerIds[0], count: 100 }
          : { count: 100 };
      const data = await getAllDispatches(params);
      const list = data.data || data.dispatches || data || [];
      setDispatchOptions(
        list
          .filter((d) => customerIds.length <= 1 || d.delivery_type === "multi_point")
          .map((d) => ({
            value: d.id,
            label: `${d.dispatch_number} — ${d.customer?.name || "Multiple Customers"} (${(d.status || "dispatched").toUpperCase()})`,
          }))
      );
    } catch {
      toast.error("Failed to load existing dispatches");
    }
    setDispatchOptionsLoading(false);
  };

  const handleExistingDispatchSubmit = async () => {
    try {
      const values = await existingDispatchForm.validateFields();
      if (
        !existingDispatchItems.length ||
        existingDispatchItems.every((i) => !i.qty_dispatched)
      ) {
        return toast.error("Enter dispatch quantity for at least one job");
      }

      const payload = {
        receipt_number: values.challan_number,
        dispatch_date: values.dispatched_on
          ? values.dispatched_on.toISOString()
          : new Date().toISOString(),
        notes: values.notes,
        items: existingDispatchItems
          .filter((i) => i.qty_dispatched > 0)
          .map((i) => ({
            job_id: i.job_id,
            qty_dispatched: i.qty_dispatched,
          })),
      };

      setSubmittingExisting(true);
      const result = await addItemsToDispatch(values.dispatch_id, payload);
      toast.success(`Items added to dispatch ${result.dispatch_number}!`);
      setExistingDispatchModalVisible(false);
      setExistingDispatchItems([]);
      existingDispatchForm.resetFields();
      setSelectedJobsByOrder({});
      fetchReady();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to add items to dispatch");
    } finally {
      setSubmittingExisting(false);
    }
  };

  const viewDispatchDetail = async (id) => {
    try {
      const detail = await getDispatchById(id);
      setDispatchDetail(detail);
      setDetailModalVisible(true);
    } catch {
      toast.error("Failed to load dispatch details");
    }
  };

  // ── Dispatch History Filters ────────────────────────────────
  const getCustomerLabel = useCallback(
    (r) => (r.delivery_type === "multi_point" ? "Multiple Customers" : r.customer?.name || "-"),
    []
  );

  const getOrderList = useCallback(
    (r) => [
      ...new Map(
        (r.items || [])
          .map((i) => i.job?.order)
          .filter((o) => o?.order_number)
          .map((o) => [o.order_number, o])
      ).values(),
    ],
    []
  );

  const getPoNumbers = useCallback(
    (r) => [
      ...new Set(
        (r.items || []).map((i) => i.job?.order?.customer_po_number).filter(Boolean)
      ),
    ],
    []
  );

  const getChallans = useCallback((r) => {
    const list = [
      ...new Set((r.items || []).map((i) => i.receipt_number).filter(Boolean)),
    ];
    return list.length ? list : r.receipt_number ? [r.receipt_number] : [];
  }, []);

  const customerFilterOptions = useMemo(() => {
    const map = new Map();
    let hasMultiPoint = false;
    dispatches.forEach((d) => {
      if (d.delivery_type === "multi_point") {
        hasMultiPoint = true;
      } else if (d.customer?.id) {
        map.set(d.customer.id, d.customer.name);
      }
    });
    const options = [...map.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
    if (hasMultiPoint) {
      options.push({ value: "__multi_point__", label: "Multiple Customers" });
    }
    return options;
  }, [dispatches]);

  const filteredDispatches = useMemo(() => {
    const searchTerm = historySearch.trim().toLowerCase();
    return dispatches.filter((d) => {
      if (historyCustomerFilter) {
        if (historyCustomerFilter === "__multi_point__") {
          if (d.delivery_type !== "multi_point") return false;
        } else if (d.customer?.id !== historyCustomerFilter) {
          return false;
        }
      }
      if (!searchTerm) return true;
      const haystack = [
        d.dispatch_number,
        getCustomerLabel(d),
        ...getOrderList(d).map((o) => o.order_number),
        ...getPoNumbers(d),
        ...getChallans(d),
        ...(d.items || []).map((i) => i.item_name),
        ...(d.items || []).map((i) => i.job_number),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(searchTerm);
    });
  }, [
    dispatches,
    historySearch,
    historyCustomerFilter,
    getCustomerLabel,
    getOrderList,
    getPoNumbers,
    getChallans,
  ]);

  // ── Stats ──────────────────────────────────────────────────
  const totalJobs = readyData?.total_jobs || 0;
  const totalOrders = readyData?.orders?.length || 0;
  const selectedJobCount = Object.values(selectedJobsByOrder).reduce(
    (total, jobIds) => total + jobIds.length,
    0
  );
  const dispatchModalWidth = screens.xs
    ? "calc(100vw - 16px)"
    : screens.sm
    ? "calc(100vw - 40px)"
    : 980;

  // ── Ready Tab Content ──────────────────────────────────────
  const ReadyContent = () => (
    <>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Jobs Ready"
              value={totalJobs}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="Orders" value={totalOrders} />
          </Card>
        </Col>
        <Col span={12} style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Button
            type="primary"
            icon={<SendOutlined />}
            size="large"
            disabled={!selectedJobCount}
            onClick={() => openDispatchForm()}
          >
            Create Dispatch
          </Button>
          <Button
            icon={<FileAddOutlined />}
            size="large"
            disabled={!selectedJobCount}
            onClick={() => openExistingDispatchForm()}
          >
            Existing Dispatch
          </Button>
        </Col>
      </Row>

      {!readyData || totalJobs === 0 ? (
        <Empty description="No jobs ready for dispatch" />
      ) : (
        readyData.orders.map((orderGroup) => (
          <Card
            key={orderGroup.order_id}
            title={
              <Space>
                <Text strong>{orderGroup.order_number}</Text>
                <Text type="secondary">{orderGroup.customer?.name}</Text>
                {orderGroup.po_number && (
                  <Tag color="default">PO: {orderGroup.po_number}</Tag>
                )}
                <Badge count={orderGroup.jobs.length} style={{ backgroundColor: "#1890ff" }} />
              </Space>
            }
            extra={
              <Space>
                <Button
                  type="primary"
                  size="small"
                  icon={<SendOutlined />}
                  disabled={!selectedJobCount}
                  onClick={() => openDispatchForm()}
                >
                  Create Dispatch
                </Button>
                <Button
                  size="small"
                  icon={<FileAddOutlined />}
                  disabled={!selectedJobCount}
                  onClick={() => openExistingDispatchForm()}
                >
                  Existing Dispatch
                </Button>
              </Space>
            }
            style={{ marginBottom: 16 }}
            size="small"
          >
            <Table
              size="small"
              dataSource={orderGroup.jobs}
              rowKey="id"
              pagination={false}
              rowSelection={{
                selectedRowKeys: selectedJobsByOrder[orderGroup.order_id] || [],
                getCheckboxProps: (record) => ({
                  disabled: (record.remaining_qty || 0) <= 0,
                }),
                onChange: (selectedRowKeys) => {
                  setSelectedJobsByOrder((prev) => ({
                    ...prev,
                    [orderGroup.order_id]: selectedRowKeys,
                  }));
                },
              }}
              columns={[
                { title: "Job #", dataIndex: "job_number", width: 130 },
                {
                  title: "Item",
                  dataIndex: "item_name",
                  ellipsis: true,
                },
                { title: "Ordered", dataIndex: "ordered_qty", width: 90 },
                {
                  title: "Dispatched",
                  dataIndex: "dispatched_qty",
                  width: 100,
                  render: (v) => v || 0,
                },
                {
                  title: "Remaining",
                  dataIndex: "remaining_qty",
                  width: 100,
                  render: (v) => (
                    <Text type={v > 0 ? "warning" : "success"} strong>
                      {v}
                    </Text>
                  ),
                },
                {
                  title: "Balance",
                  width: 90,
                  render: (_, record) =>
                    record.is_partial_dispatch ? (
                      <Tag color="orange">Partial</Tag>
                    ) : (
                      <Tag color="green">Full</Tag>
                    ),
                },
                {
                  title: "Priority",
                  dataIndex: "priority",
                  width: 90,
                  render: (p) => {
                    const colors = { urgent: "red", high: "orange", normal: "blue", low: "default" };
                    return <Tag color={colors[p] || "default"}>{p}</Tag>;
                  },
                },
              ]}
            />
          </Card>
        ))
      )}
    </>
  );

  // ── History Tab Content ────────────────────────────────────
  const HistoryContent = () => (
    <>
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={8}>
          <Input
            placeholder="Search dispatch #, order #, PO, challan, item..."
            prefix={<SearchOutlined />}
            allowClear
            value={historySearch}
            onChange={(e) => setHistorySearch(e.target.value)}
          />
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Select
            placeholder="Filter by customer"
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: "100%" }}
            value={historyCustomerFilter}
            onChange={setHistoryCustomerFilter}
            options={customerFilterOptions}
          />
        </Col>
      </Row>
      <Table
        loading={loading}
        dataSource={filteredDispatches}
        rowKey="id"
        pagination={{ pageSize: 15 }}
        columns={[
          {
            title: "Dispatch #",
            dataIndex: "dispatch_number",
            width: 150,
            sorter: (a, b) => (a.dispatch_number || "").localeCompare(b.dispatch_number || ""),
            render: (v) => <Text strong>{v}</Text>,
          },
          {
            title: "Customer",
            width: 200,
            ellipsis: true,
            sorter: (a, b) => getCustomerLabel(a).localeCompare(getCustomerLabel(b)),
            render: (_, r) => getCustomerLabel(r),
          },
          {
            title: "Order #",
            width: 200,
            sorter: (a, b) =>
              (getOrderList(a)[0]?.order_number || "").localeCompare(
                getOrderList(b)[0]?.order_number || ""
              ),
            render: (_, r) => {
              const orders = getOrderList(r);
              if (!orders.length) return "-";
              const first = orders[0];
              const extra = orders.length - 1;
              return (
                <Tooltip
                  title={
                    <Space direction="vertical" size={2}>
                      {orders.map((o) => (
                        <Text key={o.order_number} style={{ color: "#fff" }}>
                          {o.order_number}
                        </Text>
                      ))}
                    </Space>
                  }
                >
                  <Space size={4}>
                    {first.id ? (
                      <Link to={`/order/${first.id}`} onClick={(e) => e.stopPropagation()}>
                        {first.order_number}
                      </Link>
                    ) : (
                      <Text>{first.order_number}</Text>
                    )}
                    {extra > 0 && <Text type="secondary">+{extra} more</Text>}
                  </Space>
                </Tooltip>
              );
            },
          },
          {
            title: "PO Number",
            width: 160,
            sorter: (a, b) =>
              (getPoNumbers(a)[0] || "").localeCompare(getPoNumbers(b)[0] || ""),
            render: (_, r) => {
              const poNumbers = getPoNumbers(r);
              if (!poNumbers.length) return "-";
              const display = poNumbers.join(", ");
              return (
                <Tooltip title={display}>
                  <Text ellipsis style={{ maxWidth: 140, display: "inline-block" }}>
                    {display}
                  </Text>
                </Tooltip>
              );
            },
          },
          {
            title: "Challan",
            width: 130,
            sorter: (a, b) =>
              (getChallans(a)[0] || "").localeCompare(getChallans(b)[0] || ""),
            render: (_, r) => {
              const challans = getChallans(r);
              const display = challans.length ? challans.join(", ") : "-";
              return (
                <Tooltip title={display}>
                  <Text type="secondary" ellipsis style={{ maxWidth: 110, display: "inline-block" }}>
                    {display}
                  </Text>
                </Tooltip>
              );
            },
          },
          {
            title: "Status",
            dataIndex: "status",
            width: 110,
            sorter: (a, b) => (a.status || "").localeCompare(b.status || ""),
            render: (s) => (
              <Tag color={s === "completed" ? "green" : s === "in_transit" ? "blue" : "default"}>
                {(s || "created").replace(/_/g, " ").toUpperCase()}
              </Tag>
            ),
          },
          {
            title: "Date",
            dataIndex: "created_at",
            width: 120,
            sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
            defaultSortOrder: "descend",
            render: (d) => moment(d).format("DD MMM YYYY"),
          },
          {
            title: "Action",
            width: 90,
            render: (_, r) => (
              <Tooltip title="View Details">
                <Button
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => viewDispatchDetail(r.id)}
                />
              </Tooltip>
            ),
          },
        ]}
      />
    </>
  );

  // ── Render ─────────────────────────────────────────────────
  if (!isLogged) return <Navigate to="/auth/login" replace />;
  checkTokenExp(accessToken);

  return (
    <>
      <PageTitle title="Back" />
      <div className="mr-top" style={{ padding: "0 24px" }}>
        <Card bordered={false} style={{ marginBottom: 16 }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={4} style={{ margin: 0 }}>
                Dispatch Planning
              </Title>
            </Col>
            <Col>
              <Button
                icon={<ReloadOutlined spin={loading} />}
                onClick={() =>
                  activeTab === "ready" ? fetchReady() : fetchHistory()
                }
              >
                Refresh
              </Button>
            </Col>
          </Row>
        </Card>

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: "ready",
              label: (
                <span>
                  <ClockCircleOutlined /> Ready for Dispatch{" "}
                  {totalJobs > 0 && <Badge count={totalJobs} size="small" />}
                </span>
              ),
              children: loading && !readyData ? <Spin /> : <ReadyContent />,
            },
            {
              key: "history",
              label: (
                <span>
                  <CheckCircleOutlined /> Dispatch History
                </span>
              ),
              children: <HistoryContent />,
            },
          ]}
        />
      </div>

      {/* Create Dispatch Modal (reusable) */}
      <Modal
        title="Create Dispatch"
        open={dispatchModalVisible}
        onCancel={() => {
          setDispatchModalVisible(false);
          setDispatchItems([]);
          dispatchForm.resetFields();
          setReceivingFile(null);
        }}
        onOk={handleDispatchSubmit}
        okText="Save Dispatch"
        confirmLoading={submitting}
        width={dispatchModalWidth}
        style={{ top: screens.xs ? 8 : 24 }}
        bodyStyle={{
          maxHeight: screens.xs ? "calc(100vh - 140px)" : "calc(100vh - 180px)",
          overflowY: "auto",
          paddingBottom: 8,
        }}
        destroyOnClose
      >
        <Form form={dispatchForm} layout="vertical">
          {dispatchItems.length > 0 && (
            <Card size="small" style={{ marginBottom: 16 }}>
              <Space direction="vertical" size={2}>
                <Text strong>Selected Jobs: {dispatchItems.length}</Text>
                <Text type="secondary">
                  Customers: {[...new Set(dispatchItems.map((item) => item.customer_name))].join(", ")}
                </Text>
                <Text type="secondary">
                  Orders: {[...new Set(dispatchItems.map((item) => item.order_number))].join(", ")}
                </Text>
                {[...new Set(dispatchItems.map((item) => item.po_number).filter(Boolean))].length > 0 && (
                  <Text type="secondary">
                    PO #: {[...new Set(dispatchItems.map((item) => item.po_number).filter(Boolean))].join(", ")}
                  </Text>
                )}
                <Text type="secondary">
                  Dispatch quantity starts with the full remaining quantity for each job. Reduce it
                  when you need to record a partial dispatch in this challan.
                </Text>
              </Space>
            </Card>
          )}

          {dispatchItems.length > 0 && (
            <Table
              size="small"
              dataSource={dispatchItems}
              rowKey="job_id"
              pagination={false}
              bordered
              style={{ marginBottom: 16 }}
              scroll={{ x: 980 }}
              columns={[
                { title: "Job #", dataIndex: "job_number", width: 130 },
                {
                  title: "Order",
                  dataIndex: "order_number",
                  width: 130,
                },
                {
                  title: "Customer",
                  dataIndex: "customer_name",
                  width: 190,
                  ellipsis: true,
                },
                {
                  title: "Item",
                  dataIndex: "item_name",
                  width: 200,
                  ellipsis: true,
                },
                { title: "Ordered", dataIndex: "ordered_qty", width: 80 },
                {
                  title: "Dispatched",
                  dataIndex: "dispatched_qty",
                  width: 90,
                },
                {
                  title: "Remaining",
                  dataIndex: "remaining_qty",
                  width: 90,
                  render: (v) => (
                    <Text type={v > 0 ? "warning" : "success"}>{v}</Text>
                  ),
                },
                {
                  title: "Dispatch Qty",
                  width: 120,
                  render: (_, record, idx) => (
                    <InputNumber
                      size="small"
                      min={0}
                      max={record.remaining_qty}
                      precision={0}
                      value={record.qty_dispatched}
                      onChange={(val) => {
                        setDispatchItems((prev) => {
                          const next = [...prev];
                          next[idx] = {
                            ...next[idx],
                            qty_dispatched: Math.min(
                              Math.max(0, Number(val) || 0),
                              record.remaining_qty
                            ),
                          };
                          return next;
                        });
                      }}
                    />
                  ),
                },
              ]}
            />
          )}

          <Row gutter={[16, 0]}>
            <Col xs={24} md={12}>
              <Form.Item
                name="delivery_type"
                label="Dispatch Type"
                initialValue="single"
              >
                <Select>
                  <Select.Option value="single">Single Point</Select.Option>
                  <Select.Option value="multi_point">Multi Point</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="dispatched_on"
                label="Dispatched On"
                initialValue={moment()}
              >
                <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col xs={24} md={12}>
              <Form.Item
                name="challan_number"
                label="Challan Number"
                rules={[{ required: true, message: "Challan number is required" }]}
              >
                <Input placeholder="Enter challan number" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Receiving Upload">
                <Upload
                  accept="image/*"
                  beforeUpload={(file) => {
                    setReceivingFile(file);
                    return false;
                  }}
                  onRemove={() => {
                    setReceivingFile(null);
                  }}
                  maxCount={1}
                  fileList={receivingFile ? [receivingFile] : []}
                >
                  <Button
                    icon={<UploadOutlined />}
                    style={{ width: screens.xs ? "100%" : "auto" }}
                  >
                    Upload Receiving
                  </Button>
                </Upload>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="notes" label="Remarks">
            <Input.TextArea rows={3} placeholder="Enter dispatch notes" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Existing Dispatch Modal (add selected jobs to an already-created dispatch) */}
      <Modal
        title="Add to Existing Dispatch"
        open={existingDispatchModalVisible}
        onCancel={() => {
          setExistingDispatchModalVisible(false);
          setExistingDispatchItems([]);
          existingDispatchForm.resetFields();
        }}
        onOk={handleExistingDispatchSubmit}
        okText="Add to Dispatch"
        confirmLoading={submittingExisting}
        width={dispatchModalWidth}
        style={{ top: screens.xs ? 8 : 24 }}
        bodyStyle={{
          maxHeight: screens.xs ? "calc(100vh - 140px)" : "calc(100vh - 180px)",
          overflowY: "auto",
          paddingBottom: 8,
        }}
        destroyOnClose
      >
        <Form form={existingDispatchForm} layout="vertical">
          {existingDispatchItems.length > 0 && (
            <Card size="small" style={{ marginBottom: 16 }}>
              <Space direction="vertical" size={2}>
                <Text strong>Selected Jobs: {existingDispatchItems.length}</Text>
                <Text type="secondary">
                  Customers: {[...new Set(existingDispatchItems.map((item) => item.customer_name))].join(", ")}
                </Text>
                <Text type="secondary">
                  Orders: {[...new Set(existingDispatchItems.map((item) => item.order_number))].join(", ")}
                </Text>
                {[...new Set(existingDispatchItems.map((item) => item.po_number).filter(Boolean))].length > 0 && (
                  <Text type="secondary">
                    PO #: {[...new Set(existingDispatchItems.map((item) => item.po_number).filter(Boolean))].join(", ")}
                  </Text>
                )}
              </Space>
            </Card>
          )}

          <Form.Item
            name="dispatch_id"
            label="Existing Dispatch"
            rules={[{ required: true, message: "Select an existing dispatch" }]}
          >
            <Select
              placeholder="Select dispatch to add items to"
              loading={dispatchOptionsLoading}
              options={dispatchOptions}
              showSearch
              optionFilterProp="label"
              notFoundContent={
                dispatchOptionsLoading ? "Loading..." : "No matching dispatches found"
              }
            />
          </Form.Item>

          {existingDispatchItems.length > 0 && (
            <Table
              size="small"
              dataSource={existingDispatchItems}
              rowKey="job_id"
              pagination={false}
              bordered
              style={{ marginBottom: 16 }}
              scroll={{ x: 980 }}
              columns={[
                { title: "Job #", dataIndex: "job_number", width: 130 },
                {
                  title: "Order",
                  dataIndex: "order_number",
                  width: 130,
                },
                {
                  title: "Customer",
                  dataIndex: "customer_name",
                  width: 190,
                  ellipsis: true,
                },
                {
                  title: "Item",
                  dataIndex: "item_name",
                  width: 200,
                  ellipsis: true,
                },
                { title: "Ordered", dataIndex: "ordered_qty", width: 80 },
                {
                  title: "Dispatched",
                  dataIndex: "dispatched_qty",
                  width: 90,
                },
                {
                  title: "Remaining",
                  dataIndex: "remaining_qty",
                  width: 90,
                  render: (v) => (
                    <Text type={v > 0 ? "warning" : "success"}>{v}</Text>
                  ),
                },
                {
                  title: "Dispatch Qty",
                  width: 120,
                  render: (_, record, idx) => (
                    <InputNumber
                      size="small"
                      min={0}
                      max={record.remaining_qty}
                      precision={0}
                      value={record.qty_dispatched}
                      onChange={(val) => {
                        setExistingDispatchItems((prev) => {
                          const next = [...prev];
                          next[idx] = {
                            ...next[idx],
                            qty_dispatched: Math.min(
                              Math.max(0, Number(val) || 0),
                              record.remaining_qty
                            ),
                          };
                          return next;
                        });
                      }}
                    />
                  ),
                },
              ]}
            />
          )}

          <Row gutter={[16, 0]}>
            <Col xs={24} md={12}>
              <Form.Item
                name="challan_number"
                label="Challan Number"
                rules={[{ required: true, message: "Challan number is required" }]}
              >
                <Input placeholder="Enter challan number" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="dispatched_on"
                label="Dispatched On"
                initialValue={moment()}
              >
                <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="notes" label="Remarks">
            <Input.TextArea rows={3} placeholder="Enter notes" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Dispatch Detail Modal */}
      <Modal
        title={`Dispatch: ${dispatchDetail?.dispatch_number || ""}`}
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setDispatchDetail(null);
        }}
        footer={null}
        width={1200}
      >
        {dispatchDetail && (
          <>
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="Customer">
                {dispatchDetail.delivery_type === "multi_point"
                  ? "Multiple Customers"
                  : dispatchDetail.customer?.name}
              </Descriptions.Item>
              <Descriptions.Item label="Order">
                {dispatchDetail.delivery_type === "multi_point"
                  ? "Multiple Orders"
                  : dispatchDetail.items?.[0]?.job?.order?.order_number || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Challan">
                {dispatchDetail.receipt_number}
              </Descriptions.Item>
              {dispatchDetail.receiving_image_url && (
                <Descriptions.Item label="Receiving Upload">
                  <Button
                    type="link"
                    size="small"
                    onClick={() => window.open(dispatchDetail.receiving_image_url, "_blank")}
                  >
                    View Upload
                  </Button>
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Notes" span={2}>
                {dispatchDetail.notes || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Date">
                {moment(dispatchDetail.dispatch_date || dispatchDetail.created_at).format("DD MMM YYYY hh:mm A")}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag
                  color={
                    dispatchDetail.status === "completed" ? "green" : "blue"
                  }
                >
                  {(dispatchDetail.status || "created").toUpperCase()}
                </Tag>
              </Descriptions.Item>
            </Descriptions>

            <Divider orientation="left" plain>
              Items
            </Divider>
            <Table
              size="small"
              dataSource={dispatchDetail.items || []}
              rowKey="id"
              pagination={false}
              scroll={{ x: 1300 }}
              columns={[
                {
                  title: "Job #",
                  dataIndex: "job_number",
                  width: 140,
                  render: (v, r) =>
                    r.job_id ? (
                      <Link to={`/job/${r.job_id}`}>{v}</Link>
                    ) : (
                      v
                    ),
                },
                {
                  title: "Item",
                  dataIndex: "item_name",
                  width: 220,
                  ellipsis: true,
                  render: (v) => (
                    <Tooltip title={v}>
                      <Text ellipsis style={{ maxWidth: 200, display: "inline-block" }}>
                        {v}
                      </Text>
                    </Tooltip>
                  ),
                },
                {
                  title: "Order #",
                  width: 140,
                  render: (_, r) => {
                    const order = r.job?.order;
                    if (!order?.order_number) return "-";
                    return order.id ? (
                      <Link to={`/order/${order.id}`}>{order.order_number}</Link>
                    ) : (
                      order.order_number
                    );
                  },
                },
                {
                  title: "PO Number",
                  width: 130,
                  render: (_, r) => r.job?.order?.customer_po_number || "-",
                },
                { title: "Ordered", dataIndex: "ordered_qty", width: 90 },
                {
                  title: "Prev Dispatched",
                  dataIndex: "previously_dispatched",
                  width: 120,
                },
                {
                  title: "This Dispatch",
                  dataIndex: "qty_dispatched",
                  width: 110,
                  render: (v) => <Text strong>{v}</Text>,
                },
                {
                  title: "Remaining",
                  dataIndex: "qty_remaining",
                  width: 100,
                  render: (v) => (
                    <Text type={v > 0 ? "warning" : "success"} strong={v > 0}>
                      {v}
                    </Text>
                  ),
                },
                {
                  title: "Challan",
                  dataIndex: "receipt_number",
                  width: 110,
                },
                {
                  title: "Date",
                  dataIndex: "dispatch_date",
                  width: 120,
                  render: (value) =>
                    value ? moment(value).format("DD MMM YYYY") : "-",
                },
                {
                  title: "Notes",
                  dataIndex: "notes",
                  width: 160,
                  render: (value) => value || "-",
                },
              ]}
            />
          </>
        )}
      </Modal>
    </>
  );
};

export default DispatchPlanning;
